import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  Box as MuiBox,
  FormControl as MuiFormControl,
  Checkbox as MuiCheckbox,
  FormControlLabel as MuiFormControlLabel,
} from "@mui/material";
import { AdapterDateFns as MuiAdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import {
  DatePicker as MuiDatePicker,
  LocalizationProvider as MuiLocalizationProvider,
} from "@mui/x-date-pickers-pro";
import { format, isValid, parseISO } from "date-fns";
import { ResetFilter } from "app/shared/ui/FilterPanel/ResetFilter";
import { Autocomplete } from "app/shared/ui/Autocomplete/Autocomplete";
import { AutocompleteOption } from "app/shared/ui/Autocomplete";
import {
  traineeTypeDropDown,
  isActiveOptions,
  STORAGE_TYPE,
  STORAGE_NAMES,
} from "app/shared/constants";
/** Services */
import { getTraineeFilters } from "app/services/traineeService";
import { useTraineeContext } from "app/services/traineeContext";
import { useTraineesSelectedContext } from "app/services/traineesSelectedContext";
import { useSelectedSubmission } from "app/services/selectedSubmissionService";
import { useBrowserStorage } from "app/services/StorageService/StorageHelper";
/* Context and Component to show the error on UI */
import { Theme, useAlerts } from "common";
/** Styles */
import { traineeFilterStyles } from "./TraineesFilter.styles";
import { sharedDatePickerStyle } from "app/shared/ui/sharedStyles";

const initialTraineeFilterValues = {
  traineeName: "",
  departmentName: "",
  departmentCode: "",
  facultyName: "",
  traineeType: "",
  currentTrainee: "",
  trainingStartDateFrom: "",
  trainingStartDateTo: "",
  trainingEndDateFrom: "",
  trainingEndDateTo: "",
  isActive: isActiveOptions[0],
};

const TraineeFilterDef = () => {
  const { t } = useTranslation();
  const { saveItem } = useBrowserStorage(STORAGE_TYPE.SESSION);

  const { traineePreferences, setTraineePreferences } = useTraineeContext();
  const initialFilterValues = traineePreferences.traineeValues.filters;

  const [programList, setProgramList] = useState([]);
  const [traineeType, setTraineeType] = useState([]);
  const [selectedStartFromDate, setSelectedStartFromDate] = useState(
    initialFilterValues.trainingStartDateFrom
      ? parseISO(initialFilterValues.trainingStartDateFrom)
      : null,
  );
  const [selectedStartToDate, setSelectedStartToDate] = useState(
    initialFilterValues.trainingStartDateTo
      ? parseISO(initialFilterValues.trainingStartDateTo)
      : null,
  );

  const [selectedEndFromDate, setSelectedEndFromDate] = useState(
    initialFilterValues.trainingEndDateFrom
      ? parseISO(initialFilterValues.trainingEndDateFrom)
      : null,
  );
  const [selectedEndToDate, setSelectedEndToDate] = useState(
    initialFilterValues.trainingEndDateTo
      ? parseISO(initialFilterValues.trainingEndDateTo)
      : null,
  );

  const [maxDateStart, setMaxDateStart] = useState(null);
  const [maxDateEnd, setMaxDateEnd] = useState(null);

  const [minDateStart, setMinDateStart] = useState(null);
  const [minDateEnd, setMinDateEnd] = useState(null);

  const [startDateFromError, setStartDateFromError] = useState(false);
  const [startDateToError, setStartDateToError] = useState(false);
  const [endDateFromError, setEndDateFromError] = useState(false);
  const [endDateToError, setEndDateToError] = useState(false);

  const { setAlert, clearAlert } = useAlerts();

  // const traineeFilterData = new Map();
  const [traineeFiltersSelectedValues, setTraineeFiltersSelectedValues] =
    useState({ ...initialFilterValues });

  // Reset Filter Form Values
  const resetTraineeFilterValues = () => {
    saveItem(
      STORAGE_NAMES.TGDS_TRAINEES_DASHBOARD_FILTERS,
      initialTraineeFilterValues,
    );
    setTraineeFiltersSelectedValues({ ...initialTraineeFilterValues });
    setSelectedEndToDate(null);
    setSelectedEndFromDate(null);
    setSelectedStartFromDate(null);
    setSelectedStartToDate(null);
    setFilterPreference({ ...initialTraineeFilterValues });
    resetAllSelections();
    changeFilterData(initialTraineeFilterValues);
  };

  const [resStartEndDate, setResStartEndDate] = useState([]);
  // **** Selected Submission Id ****
  const [getSelectedSubmission] = useSelectedSubmission().value;
  const [getSelectedSubmissionName] = useSelectedSubmission().name;
  const { changeFilterData, submissionUpdate } = useSelectedSubmission();
  const selectedSubmission = getSelectedSubmission();
  const { resetAllSelections } = useTraineesSelectedContext();

  // ** On Page Load or Submission Update, reset Filter Values **
  useEffect(() => {
    getTraineeFilters(
      setProgramList,
      setResStartEndDate,
      setTraineeType,
      selectedSubmission,
      setAlert,
      clearAlert,
      getSelectedSubmissionName,
      t,
    );
    // eslint-disable-next-line
  }, [selectedSubmission, traineePreferences.traineeValues.addTrainee]);
  if (Object.keys(resStartEndDate).length && !minDateEnd && !minDateStart) {
    setMinDateEnd(parseISO(resStartEndDate.trainingEndDateMin));
    setMinDateStart(parseISO(resStartEndDate.trainingStartDateMin));
    setMaxDateStart(parseISO(resStartEndDate.trainingStartDateMax));
    setMaxDateEnd(parseISO(resStartEndDate.trainingEndDateMax));
  }

  // *** Helper Function to Update Context Object, on Field Value Update ***
  const setFilterPreference = (fieldUpdate) => {
    setTraineePreferences({
      ...traineePreferences,
      traineeValues: {
        ...traineePreferences.traineeValues,
        filters: {
          ...traineePreferences.traineeValues.filters,
          ...fieldUpdate,
        },
        pagination: {
          page: 0,
          pageSize: 0,
        },
        sort: {
          order: "",
          orderBy: "",
        },
      },
    });
  };

  // **** On Dropdown Value change, update search results ****
  const handleFilterValueUpdate = (fieldName, fieldValue, updateFilterData) => {
    const updatedFilterValues = {
      ...traineeFiltersSelectedValues,
      [fieldName]: fieldValue,
    };

    if (updateFilterData) {
      setFilterPreference(updatedFilterValues);
      setTraineeFiltersSelectedValues(updatedFilterValues);
    }

    resetAllSelections();
  };

  useEffect(() => {
    if (submissionUpdate) resetTraineeFilterValues();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [submissionUpdate]);

  return (
    <>
      <MuiFormControl variant="outlined">
        <Autocomplete
          id="traineesNameAutoComplete"
          key="trainees-names"
          getOptionLabel={(option) => option?.displayText ?? ""}
          value={traineeFiltersSelectedValues.traineeName}
          label={t("trainee.mainView.filters.traineeName")}
          onChange={(e, value) => {
            handleFilterValueUpdate("traineeName", value || "", true);
          }}
          autoCompleteUrlPrefix={`/submissions/${selectedSubmission}/trainees/autocomplete?context=trainees`}
          filterOptions={(options) => options}
          renderOption={(props, state, { inputValue }) => (
            <AutocompleteOption
              option={props}
              inputValue={inputValue}
              state={state}
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <Autocomplete
          id="departmentProgramAutoComplete"
          options={programList || []}
          getOptionLabel={(option) =>
            option.name ? `${option.name} (${option.code})` : ""
          }
          value={traineeFiltersSelectedValues.departmentCode}
          clientSide
          label={t("trainee.mainView.filters.participatingDepartment")}
          onChange={(e, value) => {
            handleFilterValueUpdate("departmentCode", value || "", true);
          }}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <Autocomplete
          id="facultyNameAutoComplete"
          key="faculty-names"
          getOptionLabel={(option) => option?.displayText ?? ""}
          value={traineeFiltersSelectedValues.facultyName}
          label={t("trainee.mainView.filters.faculty")}
          onChange={(e, value) => {
            handleFilterValueUpdate("facultyName", value || "", true);
          }}
          autoCompleteUrlPrefix={`/submissions/${selectedSubmission}/faculty/autocomplete?context=trainees`}
          filterOptions={(options) => options}
          renderOption={(props, state, { inputValue }) => (
            <AutocompleteOption
              option={props}
              inputValue={inputValue}
              state={state}
            />
          )}
        />
      </MuiFormControl>
      <MuiFormControl sx={sharedDatePickerStyle}>
        <label style={traineeFilterStyles.fieldTextStyle}>
          {t("trainee.mainView.filters.trainingStartDate")}
        </label>
        <MuiBox sx={traineeFilterStyles.datePickerFormControl}>
          <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              format="MM/dd/yyyy"
              minDate={minDateStart || new Date("01/01/1970")}
              maxDate={
                selectedStartToDate || maxDateStart || new Date("01/01/2100")
              }
              invalidDateMessage={t(
                "trainee.mainView.filters.invalidDateFormat",
              )}
              id="date-picker-inline-start-from"
              margin="normal"
              value={selectedStartFromDate}
              onChange={(value, { validationError }) => {
                setSelectedStartFromDate(value);
                handleFilterValueUpdate(
                  "trainingStartDateFrom",
                  (isValid(value)
                    ? format(new Date(value), "yyyy-MM-dd")
                    : "") || "",
                  true,
                );
                if (validationError === null) setStartDateFromError(null);
              }}
              onError={(error, value) => {
                if (error === "maxDate") {
                  setStartDateFromError(
                    t("trainee.edit.traineeForm.maxDateMessage"),
                  );
                } else if (error === "invalidDate") {
                  setStartDateFromError(
                    t("trainee.edit.traineeForm.invalidDateFormat"),
                  );
                } else if (!error) {
                  setStartDateFromError(null);
                } else {
                  setStartDateToError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(startDateFromError),
                  helperText: startDateFromError,
                  size: "small",
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: "14px",
                        padding: "0px 8px",
                      },
                    },
                  },
                },
              }}
            />
            <span style={traineeFilterStyles.dateSeparator}>
              {t("trainee.mainView.filters.dateSeparator")}
            </span>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              format="MM/dd/yyyy"
              minDate={
                selectedStartFromDate || minDateStart || new Date("01/01/1970")
              }
              maxDate={maxDateEnd || maxDateStart || new Date("01/01/2100")}
              invalidDateMessage={t(
                "trainee.mainView.filters.invalidDateFormat",
              )}
              id="date-picker-inline-start-to"
              margin="normal"
              value={selectedStartToDate}
              onChange={(value, { validationError }) => {
                setSelectedStartToDate(value);
                handleFilterValueUpdate(
                  "trainingStartDateTo",
                  (isValid(value)
                    ? format(new Date(value), "yyyy-MM-dd")
                    : "") || "",
                  true,
                );
                if (validationError === null) setStartDateToError(null);
              }}
              onError={(error, value) => {
                if (error === "minDate") {
                  setStartDateToError(
                    t("trainee.edit.traineeForm.minDateMessage"),
                  );
                } else if (error === "invalidDate") {
                  setStartDateToError(
                    t("trainee.edit.traineeForm.invalidDateFormat"),
                  );
                } else if (!error) {
                  setStartDateToError(null);
                } else {
                  setStartDateFromError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(startDateToError),
                  helperText: startDateToError,
                  size: "small",
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: "14px",
                        padding: "0px 8px",
                      },
                    },
                  },
                },
              }}
            />
          </MuiLocalizationProvider>
        </MuiBox>
      </MuiFormControl>
      <MuiFormControl sx={sharedDatePickerStyle}>
        <label style={traineeFilterStyles.fieldTextStyle}>
          {t("trainee.mainView.filters.trainingEndDate")}
        </label>
        <MuiBox sx={traineeFilterStyles.datePickerFormControl}>
          <MuiLocalizationProvider dateAdapter={MuiAdapterDateFns}>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              format="MM/dd/yyyy"
              minDate={minDateStart || new Date("01/01/1970")}
              maxDate={
                selectedEndToDate || maxDateStart || new Date("01/01/2100")
              }
              invalidDateMessage={t(
                "trainee.mainView.filters.invalidDateFormat",
              )}
              margin="normal"
              id="date-picker-inline-end-from"
              value={selectedEndFromDate}
              onChange={(value, { validationError }) => {
                setSelectedEndFromDate(value);
                handleFilterValueUpdate(
                  "trainingEndDateFrom",
                  (isValid(value)
                    ? format(new Date(value), "yyyy-MM-dd")
                    : "") || "",
                  true,
                );
                if (validationError === null) setEndDateFromError(null);
              }}
              onError={(error, value) => {
                if (error === "maxDate") {
                  setEndDateFromError(
                    t("trainee.edit.traineeForm.maxDateMessage"),
                  );
                } else if (error === "invalidDate") {
                  setEndDateFromError(
                    t("trainee.edit.traineeForm.invalidDateFormat"),
                  );
                } else if (!error) {
                  setEndDateFromError(null);
                } else {
                  setEndDateToError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(endDateFromError),
                  helperText: endDateFromError,
                  size: "small",
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: "14px",
                        padding: "0px 8px",
                      },
                    },
                  },
                },
              }}
            />
            <span style={traineeFilterStyles.dateSeparator}>
              {t("trainee.mainView.filters.dateSeparator")}
            </span>
            <MuiDatePicker
              inputVariant="outlined"
              clearable
              format="MM/dd/yyyy"
              margin="normal"
              minDate={
                selectedEndFromDate || minDateStart || new Date("01/01/1970")
              }
              maxDate={maxDateEnd || maxDateStart || new Date("01/01/2100")}
              invalidDateMessage={t(
                "trainee.mainView.filters.invalidDateFormat",
              )}
              id="date-picker-inline-end-to"
              value={selectedEndToDate}
              onChange={(value, { validationError }) => {
                setSelectedEndToDate(value);
                handleFilterValueUpdate(
                  "trainingEndDateTo",
                  (isValid(value)
                    ? format(new Date(value), "yyyy-MM-dd")
                    : "") || "",
                  true,
                );
                if (validationError === null) setEndDateToError(null);
              }}
              onError={(error, value) => {
                if (error === "minDate") {
                  setEndDateToError(
                    t("trainee.edit.traineeForm.minDateMessage"),
                  );
                } else if (error === "invalidDate") {
                  setEndDateToError(
                    t("trainee.edit.traineeForm.invalidDateFormat"),
                  );
                } else if (!error) {
                  setEndDateToError(null);
                } else {
                  setEndDateFromError(null);
                }
              }}
              slotProps={{
                textField: {
                  variant: "outlined",
                  error: Boolean(endDateToError),
                  helperText: endDateToError,
                  size: "small",
                  slotProps: {
                    input: {
                      sx: {
                        fontSize: "14px",
                        padding: "0px 8px",
                      },
                    },
                  },
                },
              }}
            />
          </MuiLocalizationProvider>
        </MuiBox>
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <Autocomplete
          id="traineeTypeFilterSelect"
          options={traineeType || []}
          getOptionLabel={(option) => t(traineeTypeDropDown[option])}
          clearOnEscape
          value={
            traineeFiltersSelectedValues.traineeType
              ? t(traineeFiltersSelectedValues.traineeType)
              : ""
          }
          clientSide
          label={t("trainee.mainView.filters.traineeType")}
          onChange={(e, value) => {
            handleFilterValueUpdate("traineeType", value || "", true);
          }}
        />
      </MuiFormControl>
      <MuiFormControl variant="outlined">
        <Autocomplete
          id="isActive"
          options={isActiveOptions}
          getOptionLabel={(option) => option && t(option.label)}
          clearOnEscape
          value={traineeFiltersSelectedValues.isActive}
          clientSide
          label={t("trainee.mainView.filters.isActive")}
          onChange={(e, value) => {
            handleFilterValueUpdate("isActive", value ?? null, true);
          }}
        />
      </MuiFormControl>
      <MuiFormControlLabel
        sx={{
          "&&": {
            "& .MuiTypography-root.MuiFormControlLabel-label.MuiTypography-body1":
              {
                color: Theme.palette.grey[700],
              },
          },
        }}
        control={
          <MuiCheckbox
            sx={traineeFilterStyles.checkBoxRoot}
            name="isCurrentTrainee"
            checked={!!traineeFiltersSelectedValues.currentTrainee}
            size="small"
            onChange={(event) => {
              let current_trainee = event.target.checked;

              handleFilterValueUpdate(
                "currentTrainee",
                current_trainee || "",
                true,
              );
            }}
          />
        }
        label={t("trainee.mainView.filters.currentTrainee")}
      />
      <ResetFilter resetFn={resetTraineeFilterValues} />
    </>
  );
};

export const TraineesFilter = TraineeFilterDef;
